// Client-side image compression using HTML Canvas
// Reduces mobile photos (12MP+) to web-optimized webp (~300-500KB)

export interface CompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.0 - 1.0 for webp
  outputType?: 'webp' | 'jpeg';
}

const DEFAULTS: CompressOptions = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.75,
  outputType: 'webp',
};

export async function compressImage(
  file: File,
  options: CompressOptions = {},
): Promise<Blob> {
  const opts = { ...DEFAULTS, ...options };

  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.src = reader.result as string;
    };

    img.onload = () => {
      // Calculate target dimensions
      let { width, height } = img;
      const { maxWidth, maxHeight } = opts;

      if (maxWidth && width > maxWidth) {
        height = Math.round(height * (maxWidth / width));
        width = maxWidth;
      }
      if (maxHeight && height > maxHeight) {
        width = Math.round(width * (maxHeight / height));
        height = maxHeight;
      }

      // Draw to canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context not available')); return; }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to compressed blob
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Compression failed')); return; }
          resolve(blob);
        },
        `image/${opts.outputType}`,
        opts.quality,
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Convenience: compress and return as File with new name
export async function compressImageAsFile(
  file: File,
  options?: CompressOptions,
): Promise<File> {
  const blob = await compressImage(file, options);
  const ext = options?.outputType || 'webp';
  const name = file.name.replace(/\.[^.]+$/, `.${ext}`);
  return new File([blob], name, { type: `image/${ext}` });
}

// Batch compress multiple files with progress callback
export async function compressImages(
  files: File[],
  onProgress?: (done: number, total: number) => void,
  options?: CompressOptions,
): Promise<File[]> {
  const results: File[] = [];
  for (let i = 0; i < files.length; i++) {
    const compressed = await compressImageAsFile(files[i], options);
    results.push(compressed);
    onProgress?.(i + 1, files.length);
  }
  return results;
}
